import React, {useState, useRef, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import styled from 'styled-components';
import FastModal from '../../../components/FastModal';
import withIntl from '../../../lib/withIntl';

import './ChooseQuadrantOnSwapLabwareModal.scss';

import {setIsAnyModalShow} from '../../../stores/actions/appStateAction';

const QuadrantButtonWrapped = styled.div`
  width: fit-content;
  height: fit-content;

  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently*/
  display: grid;
  justify-content: center;
  align-content: center;
  text-align: center;
  grid-column-end: 2;
  grid-row-end: 2;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  margin-bottom: 15px;
`;

const QuadrantButton = styled.button`
  width: 50px;
  height: 50px;
  line-height: 50px;
  text-align: center;
  vertical-align: midle;
  margin: 16px !important;
  border: 1px solid #808080;
  background-color: transparent;
  color: #808080 !important;
  border-radius: 2px;
  font-weight: bold !important;
  -moz-transition: all 0.2s ease-in;
  -o-transition: all 0.2s ease-in;
  -webkit-transition: all 0.2s ease-in;
  transition: all 0.2s ease-in;
`;

const ChooseQuadrantOnPlateSwabModal = React.forwardRef(({intl, appStateActionSetIsAnyModalShow}, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [quadrantStatus, setQuadrantStatus] = useState({});
  const promiseRef = useRef({});
  const [isMultiQuadrantEnabled, setIsMultiQuadrantEnabled] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
  });
  const [additionalMessage, setAdditionalMessage] = useState(null);

  const onQuadrantClick = (id) => {
    if (isButtonDisabled[id]) {
      return;
    }

    if (isMultiQuadrantEnabled) {
      setQuadrantStatus({
        ...quadrantStatus,
        [id]: !quadrantStatus[id],
      });
      return;
    }

    const newQuadrantStatus = {
      ...quadrantStatus,
    };

    Object.keys(newQuadrantStatus).forEach((key) => {
      newQuadrantStatus[key] = false;
    });

    setQuadrantStatus({
      ...newQuadrantStatus,
      [id]: true,
    });
  };

  const allowToApply = () => !Object.values(quadrantStatus).every((value) => value === false);

  function onApply() {
    if (!showModal) {
      return;
    }

    if (!allowToApply()) {
      return;
    }

    const {resolve} = promiseRef.current;

    resolve(quadrantStatus);
    setShowModal(false);
    appStateActionSetIsAnyModalShow(false);
  }

  function onCancel() {
    const {resolve} = promiseRef.current;

    resolve(null);
    setShowModal(false);
    appStateActionSetIsAnyModalShow(false);
  }

  const renderContent = () => (
    <div className="modal-choose-quadrant-on-plate-swab">
      <QuadrantButtonWrapped className="quadrant-button-wrapper">
        <QuadrantButton
          className={`quadrant-button ${quadrantStatus[4] ? 'selected' : 'not-selected'} ${isButtonDisabled[4] ? 'disabled' : 'enabled'}`}
          onClick={() => onQuadrantClick(4)}
        >
          4
        </QuadrantButton>
        <QuadrantButton
          className={`quadrant-button ${quadrantStatus[1] ? 'selected' : 'not-selected'} ${isButtonDisabled[1] ? 'disabled' : 'enabled'}`}
          onClick={() => onQuadrantClick(1)}
        >
          1
        </QuadrantButton>
        <QuadrantButton
          className={`quadrant-button ${quadrantStatus[3] ? 'selected' : 'not-selected'} ${isButtonDisabled[3] ? 'disabled' : 'enabled'}`}
          onClick={() => onQuadrantClick(3)}
        >
          3
        </QuadrantButton>
        <QuadrantButton
          className={`quadrant-button ${quadrantStatus[2] ? 'selected' : 'not-selected'} ${isButtonDisabled[2] ? 'disabled' : 'enabled'}`}
          onClick={() => onQuadrantClick(2)}
        >
          2
        </QuadrantButton>
      </QuadrantButtonWrapped>
      {additionalMessage}
    </div>
  );

  const btnAction = [
    {
      text: intl.formatMessage({id: 'modal.plateSwab.chooseQuadrant.firstButton'}),
      style: 'primary',
      handlerClick: onApply,
      disabled: !allowToApply(),
    },
    {
      text: intl.formatMessage({id: 'modal.plateSwab.chooseQuadrant.secondButton'}),
      style: 'secondary',
      handlerClick: onCancel,
    },
  ];

  useImperativeHandle(ref, () => ({
    show(initialQuadrantStatus = {}, enableMultiQuadrant = false, enabledButton = [], additionalMsg = null) {
      return new Promise((resolve, reject) => {
        promiseRef.current = {resolve, reject};

        setQuadrantStatus(initialQuadrantStatus);
        setShowModal(true);
        appStateActionSetIsAnyModalShow(true);
        setIsMultiQuadrantEnabled(enableMultiQuadrant);

        if (enabledButton.length) {
          setIsButtonDisabled({
            1: !enabledButton.includes(1),
            2: !enabledButton.includes(2),
            3: !enabledButton.includes(3),
            4: !enabledButton.includes(4),
          });
        } else {
          setIsButtonDisabled({
            1: false,
            2: false,
            3: false,
            4: false,
          });
        }
        setAdditionalMessage(additionalMsg);
      });
    },
    hide() {
      return new Promise((resolve, reject) => {
        promiseRef.current = {resolve, reject};

        setShowModal(false);
        appStateActionSetIsAnyModalShow(false);
      });
    },
  }));

  return (
    <FastModal
      ref={ref}
      header={intl.formatMessage({id: 'modal.plateSwab.chooseQuadrant.title'})}
      content={renderContent()}
      btnAction={btnAction}
      show={showModal}
      className="modal-choose-quadrant-on-plate-swab"
      isValid
      closeDisabled
      size="sm"
    />
  );
});

const mapDispatchToProps = (dispatch) => ({
  appStateActionSetIsAnyModalShow: bindActionCreators(setIsAnyModalShow, dispatch),
});

ChooseQuadrantOnPlateSwabModal.propTypes = {
  appStateActionSetIsAnyModalShow: PropTypes.any.isRequired,
  intl: PropTypes.any.isRequired,
};

export default connect(null, mapDispatchToProps, null, {forwardRef: true})(withIntl(ChooseQuadrantOnPlateSwabModal));
